import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Calculator, FileBarChart, PackageMinus, Pencil, Plus, ShieldCheck, Trash2, Truck,
} from 'lucide-react';
import { AccesRefuse, AlertBanner, Button, KPICard, Modal, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import type { StatusKey } from '@/types';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/FormCard';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type CategorieImmo, type CessionInput, type Immobilisation, type ImmobilisationInput,
  type ImmobilisationStatut, type ModeFinancement, type ModeSortie,
  capitaliserParc, ceder, certifierPlan, createImmobilisation, cumulADate, deleteImmobilisation,
  genererDotations, getPlan, getReconciliation, listImmobilisations, planParAnnee, updateImmobilisation,
} from '@/services/immobilisations';
import { LeasingTab } from './LeasingTab';
import { toast } from '@/store/toastStore';
import { formatDZD, formatDateDZ } from '@/utils/algeria';

const FIN_LABEL: Record<ModeFinancement, string> = {
  FONDS_PROPRES: 'Fonds propres', CREDIT: 'Crédit bancaire', CREDIT_BAIL: 'Crédit-bail (leasing)',
};

const CAT_LABEL: Record<CategorieImmo, string> = {
  CONSTRUCTIONS: 'Constructions (213)',
  MATERIEL: 'Matériel & outillage (215)',
  AUTRES: 'Autres corporelles (218)',
};
const STATUT_UI: Record<ImmobilisationStatut, { label: string; status: StatusKey }> = {
  EN_SERVICE: { label: 'En service', status: 'done' },
  CEDE: { label: 'Cédé', status: 'info' },
  MIS_AU_REBUT: { label: 'Mis au rebut', status: 'stuck' },
};
const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);

type FormState = {
  code: string; libelle: string; categorie: CategorieImmo; fournisseur: string;
  date_acquisition: string; date_mise_en_service: string;
  valeur_acquisition_ht: number; tva_deductible: number; mode_financement: ModeFinancement;
  valeur_residuelle: number; duree_ans: number; notes: string;
};
const formVide = (): FormState => ({
  code: '', libelle: '', categorie: 'MATERIEL', fournisseur: '',
  date_acquisition: today(), date_mise_en_service: today(),
  valeur_acquisition_ht: 0, tva_deductible: 0, mode_financement: 'FONDS_PROPRES',
  valeur_residuelle: 0, duree_ans: 5, notes: '',
});

export function ImmobilisationsPage() {
  const perms = usePerms('comptabilite');
  const qc = useQueryClient();
  const [tab, setTab] = useState('registre');

  if (!perms.canConsulter) {
    return <AccesRefuse detail="Le registre des immobilisations requiert la permission « Comptabilité SCF »." />;
  }

  return (
    <>
      <PageHeader
        title="Immobilisations & amortissements"
        subtitle="Registre patrimonial des actifs — valeur, plan d'amortissement, VNC, cession"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Immobilisations' }]}
      />
      <Tabs
        className="mb-4"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'registre', label: 'Registre' },
          { key: 'leasing', label: 'Crédit-bail' },
          { key: 'controle', label: 'Contrôle & dotations' },
        ]}
      />
      {tab === 'registre' && <Registre perms={perms} qc={qc} />}
      {tab === 'leasing' && <LeasingTab perms={perms} />}
      {tab === 'controle' && <Controle />}
    </>
  );
}

/* ------------------------------------------------------------------ Registre */
function Registre({ perms, qc }: { perms: ReturnType<typeof usePerms>; qc: ReturnType<typeof useQueryClient> }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(formVide());
  const [planImmo, setPlanImmo] = useState<Immobilisation | null>(null);
  const [cederImmo, setCederImmo] = useState<Immobilisation | null>(null);

  const { data: items, isLoading } = useQuery({ queryKey: ['immobilisations'], queryFn: listImmobilisations });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['immobilisations'] });
  const set = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));

  const toPayload = (): ImmobilisationInput => ({
    code: form.code.trim(), libelle: form.libelle.trim(), categorie: form.categorie,
    fournisseur: form.fournisseur.trim() || null,
    date_acquisition: form.date_acquisition || null, date_mise_en_service: form.date_mise_en_service || null,
    valeur_acquisition_ht: Number(form.valeur_acquisition_ht) || 0,
    tva_deductible: Number(form.tva_deductible) || 0,
    mode_financement: form.mode_financement,
    valeur_residuelle: Number(form.valeur_residuelle) || 0,
    duree_mois: Math.round((Number(form.duree_ans) || 0) * 12),
    notes: form.notes.trim() || null,
  });

  const saveMut = useMutation({
    mutationFn: () => (editId ? updateImmobilisation(editId, toPayload()) : createImmobilisation(toPayload())),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editId ? 'Immobilisation modifiée.' : 'Immobilisation créée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteImmobilisation(id),
    onSuccess: () => { invalidate(); toast.success('Immobilisation supprimée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const capMut = useMutation({
    mutationFn: capitaliserParc,
    onSuccess: (r) => {
      invalidate();
      toast.success(`Parc capitalisé : ${r.inscrits} nouveau(x), ${r.deja_inscrits} déjà inscrit(s), ${r.ouvertures_comptabilisees} ouverture(s) comptabilisée(s).`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const ouvrirCreation = () => { setEditId(null); setForm(formVide()); setOpen(true); };
  const ouvrirEdition = (im: Immobilisation) => {
    setEditId(im.id);
    setForm({
      code: im.code, libelle: im.libelle, categorie: im.categorie, fournisseur: im.fournisseur ?? '',
      date_acquisition: im.date_acquisition ?? '', date_mise_en_service: im.date_mise_en_service ?? '',
      valeur_acquisition_ht: Number(im.valeur_acquisition_ht), valeur_residuelle: Number(im.valeur_residuelle),
      tva_deductible: Number(im.tva_deductible ?? 0), mode_financement: im.mode_financement ?? 'FONDS_PROPRES',
      duree_ans: im.duree_mois ? im.duree_mois / 12 : 0, notes: im.notes ?? '',
    });
    setOpen(true);
  };

  const rows = items ?? [];
  const enService = rows.filter((r) => r.statut === 'EN_SERVICE');
  const totalBrut = enService.reduce((s, r) => s + Number(r.valeur_acquisition_ht), 0);
  const sansDuree = enService.filter((r) => !r.duree_mois).length;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
          <KPICard title="Biens en service" value={String(enService.length)} icon={FileBarChart} />
          <KPICard title="Valeur brute (en service)" value={formatDZD(totalBrut, { decimals: 0 })} icon={Truck} />
          <KPICard title="Sans durée (à arbitrer)" value={String(sansDuree)} icon={AlertTriangle}
            accent={sansDuree ? '#c0392b' : undefined} />
        </div>
      </div>

      {perms.canModifier && (
        <div className="mb-4 flex flex-wrap gap-2">
          <Button onClick={ouvrirCreation}><Plus /> Nouvelle immobilisation</Button>
          <Button variant="secondary" loading={capMut.isPending}
            onClick={() => { if (window.confirm("Inscrire au registre tous les engins du parc ayant une valeur d'acquisition, et poser leur balance d'ouverture (brouillon) ? Idempotent.")) capMut.mutate(); }}>
            <Truck /> Capitaliser le parc
          </Button>
        </div>
      )}

      {sansDuree > 0 && (
        <AlertBanner variant="warning" title={`${sansDuree} bien(s) sans durée d'amortissement`}>
          Renseignez leur durée pour générer les dotations — aucune dotation n'est jamais devinée.
        </AlertBanner>
      )}

      {isLoading ? (
        <Skeleton className="mt-4 h-64 w-full" />
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">
          Aucune immobilisation. {perms.canModifier ? 'Créez-en une, ou « Capitaliser le parc » pour reprendre les engins.' : ''}
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 label-caps">Code</th>
                <th className="px-4 py-3 label-caps">Désignation</th>
                <th className="px-4 py-3 label-caps">Catégorie</th>
                <th className="px-4 py-3 text-right label-caps">Valeur brute</th>
                <th className="px-4 py-3 text-right label-caps">Durée</th>
                <th className="px-4 py-3 label-caps">Mise en service</th>
                <th className="px-4 py-3 label-caps">Statut</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((im) => {
                const st = STATUT_UI[im.statut];
                const actif = im.statut === 'EN_SERVICE';
                return (
                  <tr key={im.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-semibold text-content-primary">
                      {im.code}
                      {im.engin_source_id && <span className="ml-1 text-xs font-normal text-content-muted">· parc</span>}
                    </td>
                    <td className="px-4 py-2 text-content-secondary">{im.libelle}</td>
                    <td className="px-4 py-2 text-content-secondary">{CAT_LABEL[im.categorie]}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatDZD(Number(im.valeur_acquisition_ht), { decimals: 0 })}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {im.duree_mois ? `${(im.duree_mois / 12).toLocaleString('fr-DZ', { maximumFractionDigits: 1 })} ans` : <span className="text-status-stuck">à définir</span>}
                    </td>
                    <td className="px-4 py-2 text-content-secondary">{im.date_mise_en_service ? formatDateDZ(im.date_mise_en_service) : '—'}</td>
                    <td className="px-4 py-2"><StatusBadge status={st.status} label={st.label} /></td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Plan d'amortissement"
                          onClick={() => setPlanImmo(im)} aria-label="Plan"><FileBarChart className="size-4" /></Button>
                        {perms.canModifier && actif && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Modifier"
                            onClick={() => ouvrirEdition(im)} aria-label="Modifier"><Pencil className="size-4" /></Button>
                        )}
                        {perms.canModifier && actif && Number(im.valeur_acquisition_ht) > 0 && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Céder / mettre au rebut"
                            onClick={() => setCederImmo(im)} aria-label="Céder"><PackageMinus className="size-4" /></Button>
                        )}
                        {perms.canSupprimer && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" title="Supprimer"
                            onClick={() => { if (window.confirm('Supprimer cette immobilisation ?')) delMut.mutate(im.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Création / édition */}
      <Modal open={open} onOpenChange={setOpen} title={editId ? "Modifier l'immobilisation" : 'Nouvelle immobilisation'} size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!form.code.trim() || !form.libelle.trim()}>
              {editId ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Code"><Input value={form.code} disabled={!!editId} onChange={(e) => set({ code: e.target.value })} placeholder="IMMO-001" /></FormField>
          <FormField label="Désignation"><Input value={form.libelle} onChange={(e) => set({ libelle: e.target.value })} placeholder="Pelle hydraulique…" /></FormField>
          <FormField label="Catégorie">
            <select className={selectCls} value={form.categorie} onChange={(e) => set({ categorie: e.target.value as CategorieImmo })}>
              {(['MATERIEL', 'CONSTRUCTIONS', 'AUTRES'] as CategorieImmo[]).map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
            </select>
          </FormField>
          <FormField label="Fournisseur"><Input value={form.fournisseur} onChange={(e) => set({ fournisseur: e.target.value })} /></FormField>
          <FormField label="Mode de financement">
            <select className={selectCls} value={form.mode_financement} onChange={(e) => set({ mode_financement: e.target.value as ModeFinancement })}>
              {(['FONDS_PROPRES', 'CREDIT', 'CREDIT_BAIL'] as ModeFinancement[]).map((m) => <option key={m} value={m}>{FIN_LABEL[m]}</option>)}
            </select>
          </FormField>
          <FormField label="Date d'acquisition"><Input type="date" value={form.date_acquisition} onChange={(e) => set({ date_acquisition: e.target.value })} /></FormField>
          <FormField label="Date de mise en service"><Input type="date" value={form.date_mise_en_service} onChange={(e) => set({ date_mise_en_service: e.target.value })} /></FormField>
          <FormField label="Valeur d'acquisition HT"><Input type="number" step="1" value={form.valeur_acquisition_ht} onChange={(e) => set({ valeur_acquisition_ht: Number(e.target.value) })} /></FormField>
          <FormField label="TVA déductible"><Input type="number" step="1" value={form.tva_deductible} onChange={(e) => set({ tva_deductible: Number(e.target.value) })} /></FormField>
          <FormField label="Valeur résiduelle"><Input type="number" step="1" value={form.valeur_residuelle} onChange={(e) => set({ valeur_residuelle: Number(e.target.value) })} /></FormField>
          <FormField label="Durée d'amortissement (années)">
            <Input type="number" step="1" value={form.duree_ans} onChange={(e) => set({ duree_ans: Number(e.target.value) })} />
            <p className="mt-1 text-xs text-content-muted">= {Math.round((Number(form.duree_ans) || 0) * 12)} mois · linéaire, prorata temporis. 0 = pas de dotation (à arbitrer).</p>
          </FormField>
          <FormField label="Notes" className="sm:col-span-2"><Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></FormField>
        </div>
      </Modal>

      {planImmo && <PlanModal immo={planImmo} onClose={() => setPlanImmo(null)} />}
      {cederImmo && <CederModal immo={cederImmo} onClose={() => setCederImmo(null)} onDone={() => { setCederImmo(null); invalidate(); }} />}
    </>
  );
}

/* ------------------------------------------------------ Plan d'amortissement */
function PlanModal({ immo, onClose }: { immo: Immobilisation; onClose: () => void }) {
  const { data: plan, isLoading } = useQuery({ queryKey: ['immo-plan', immo.id], queryFn: () => getPlan(immo.id) });
  const annuel = useMemo(() => planParAnnee(plan ?? []), [plan]);

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Plan d'amortissement — ${immo.code}`} size="lg">
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : annuel.length === 0 ? (
        <AlertBanner variant="info">
          Aucun plan : durée non renseignée, base nulle ou bien sorti. Renseignez la durée d'amortissement pour générer le plan.
        </AlertBanner>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-3 py-2 label-caps">Exercice</th>
                <th className="px-3 py-2 text-right label-caps">Dotation</th>
                <th className="px-3 py-2 text-right label-caps">Cumul</th>
                <th className="px-3 py-2 text-right label-caps">VNC (fin)</th>
              </tr>
            </thead>
            <tbody>
              {annuel.map((a) => (
                <tr key={a.annee} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5 font-medium">{a.annee}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatDZD(a.dotation, { decimals: 0 })}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(a.cumul, { decimals: 0 })}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatDZD(a.vnc, { decimals: 0 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-content-muted">Méthode linéaire, prorata temporis à la mise en service ; dernière période soldée à la valeur résiduelle.</p>
    </Modal>
  );
}

/* --------------------------------------------------------- Cession / rebut */
function CederModal({ immo, onClose, onDone }: { immo: Immobilisation; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<ModeSortie>('CESSION');
  const [dateCession, setDateCession] = useState(today());
  const [prix, setPrix] = useState(0);
  const [motif, setMotif] = useState('');

  const { data: plan } = useQuery({ queryKey: ['immo-plan', immo.id], queryFn: () => getPlan(immo.id) });
  const brut = Number(immo.valeur_acquisition_ht);
  const [y, m] = dateCession ? [Number(dateCession.slice(0, 4)), Number(dateCession.slice(5, 7))] : [0, 0];
  const cumul = useMemo(() => cumulADate(plan ?? [], y, m), [plan, y, m]);
  const vnc = Math.max(brut - cumul, 0);
  const pmv = (mode === 'CESSION' ? Number(prix) || 0 : 0) - vnc;

  const cederMut = useMutation({
    mutationFn: () => {
      const payload: CessionInput = { mode, date_cession: dateCession, prix_cession: mode === 'CESSION' ? Number(prix) || 0 : 0, motif: motif.trim() || null };
      return ceder(immo.id, payload);
    },
    onSuccess: () => { toast.success(mode === 'CESSION' ? 'Cession enregistrée (écriture brouillon).' : 'Mise au rebut enregistrée (écriture brouillon).'); onDone(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Sortie du registre — ${immo.code}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={() => cederMut.mutate()} loading={cederMut.isPending}>{mode === 'CESSION' ? 'Céder' : 'Mettre au rebut'}</Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Type de sortie">
            <select className={selectCls} value={mode} onChange={(e) => setMode(e.target.value as ModeSortie)}>
              <option value="CESSION">Cession (vente)</option>
              <option value="REBUT">Mise au rebut (réforme)</option>
            </select>
          </FormField>
          <FormField label="Date de sortie"><Input type="date" value={dateCession} onChange={(e) => setDateCession(e.target.value)} /></FormField>
          {mode === 'CESSION' && (
            <FormField label="Prix de cession HT"><Input type="number" step="1" value={prix} onChange={(e) => setPrix(Number(e.target.value))} /></FormField>
          )}
          <FormField label="Motif (optionnel)" className={mode === 'CESSION' ? '' : 'sm:col-span-2'}><Input value={motif} onChange={(e) => setMotif(e.target.value)} /></FormField>
        </div>

        <div className="rounded-lg border border-border bg-surface-primary p-3 text-sm">
          <div className="flex justify-between"><span className="text-content-secondary">Valeur brute</span><span className="tabular-nums">{formatDZD(brut, { decimals: 0 })}</span></div>
          <div className="flex justify-between"><span className="text-content-secondary">Amortissements cumulés (à la date)</span><span className="tabular-nums">− {formatDZD(cumul, { decimals: 0 })}</span></div>
          <div className="mt-1 flex justify-between border-t border-border pt-1 font-medium"><span>VNC à la sortie</span><span className="tabular-nums">{formatDZD(vnc, { decimals: 0 })}</span></div>
          <div className={`mt-1 flex justify-between font-semibold ${pmv >= 0 ? 'text-status-done' : 'text-status-stuck'}`}>
            <span>{pmv >= 0 ? 'Plus-value' : 'Moins-value'}</span>
            <span className="tabular-nums">{formatDZD(pmv, { decimals: 0 })}</span>
          </div>
        </div>
        <p className="text-xs text-content-muted">Écriture de sortie générée en brouillon : solde 28x / sortie 21x, créance 462 et résultat (752 plus-value / 652 moins-value). À valider en comptabilité.</p>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------ Contrôle & dotations */
function Controle() {
  const qc = useQueryClient();
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [genOpen, setGenOpen] = useState(false);
  const [genMois, setGenMois] = useState(new Date().getMonth() + 1);
  const [genAnnee, setGenAnnee] = useState(new Date().getFullYear());

  const { data: cert } = useQuery({ queryKey: ['immo-certif'], queryFn: certifierPlan });
  const { data: rec, isLoading } = useQuery({ queryKey: ['immo-recon', annee], queryFn: () => getReconciliation(annee) });

  const genMut = useMutation({
    mutationFn: () => genererDotations(genMois, genAnnee),
    onSuccess: (r) => {
      if (!r.plan_certifie) { toast.error('Plan non certifié : ' + (r.problemes ?? []).join(' · ')); return; }
      qc.invalidateQueries({ queryKey: ['immo-recon'] });
      toast.success(`Dotations ${genMois}/${genAnnee} : ${r.dotations_creees} créée(s), ${r.dotations_deja} déjà présente(s), ${r.sans_duree} sans durée.`);
      setGenOpen(false);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const equilibre = rec ? Number(rec.ecart_equilibre) === 0 : true;

  return (
    <div className="space-y-4">
      {cert && !cert.plan_certifie && (
        <AlertBanner variant="error" title="Plan comptable incomplet">
          <ul className="list-disc pl-5">{cert.problemes.map((p) => <li key={p}>{p}</li>)}</ul>
        </AlertBanner>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <FormField label="Exercice" className="w-40">
          <Input type="number" step="1" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} />
        </FormField>
        <Button variant="secondary" onClick={() => setGenOpen(true)}><Calculator /> Générer les dotations</Button>
      </div>

      {isLoading || !rec ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <AlertBanner variant={equilibre ? 'success' : 'warning'} title={equilibre ? 'Équilibre vérifié : débit 681 = crédit 28x' : 'Déséquilibre détecté (681 ≠ 28x)'}>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span>Dotations de l'exercice (plan) : <b className="tabular-nums">{formatDZD(Number(rec.plan_dotations_exercice), { decimals: 0 })}</b></span>
              <span>Débit 681 : <b className="tabular-nums">{formatDZD(Number(rec.debit_681), { decimals: 0 })}</b></span>
              <span>Crédit 28x : <b className="tabular-nums">{formatDZD(Number(rec.credit_28x), { decimals: 0 })}</b></span>
              {Number(rec.ecart_dotation) !== 0 && <span className="text-status-stuck">Écart plan/écritures : {formatDZD(Number(rec.ecart_dotation), { decimals: 0 })} (périodes non générées)</span>}
            </div>
          </AlertBanner>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KPICard title="Valeur brute (21x)" value={formatDZD(Number(rec.total_brut_21x), { decimals: 0 })} icon={Truck} />
            <KPICard title="Amortissements cumulés (28x)" value={formatDZD(Number(rec.total_cumul_28x), { decimals: 0 })} icon={ShieldCheck} />
            <KPICard title="Valeur nette comptable" value={formatDZD(Number(rec.total_vnc), { decimals: 0 })} icon={FileBarChart} />
          </div>

          {rec.biens_sans_duree.length > 0 && (
            <AlertBanner variant="warning" title={`${rec.biens_sans_duree.length} bien(s) sans durée d'amortissement`}>
              {rec.biens_sans_duree.map((b) => b.code).join(', ')} — aucune dotation tant que la durée n'est pas renseignée.
            </AlertBanner>
          )}

          {rec.biens.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-surface-primary">
                  <tr className="border-b border-border text-left">
                    <th className="px-4 py-3 label-caps">Code</th>
                    <th className="px-4 py-3 label-caps">Catégorie</th>
                    <th className="px-4 py-3 text-right label-caps">Brut</th>
                    <th className="px-4 py-3 text-right label-caps">Dotation {annee}</th>
                    <th className="px-4 py-3 text-right label-caps">Cumul fin {annee}</th>
                    <th className="px-4 py-3 text-right label-caps">VNC fin {annee}</th>
                  </tr>
                </thead>
                <tbody>
                  {rec.biens.map((b) => (
                    <tr key={b.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-semibold">{b.code}</td>
                      <td className="px-4 py-2 text-content-secondary">{b.categorie}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatDZD(Number(b.brut), { decimals: 0 })}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatDZD(Number(b.dotation_exercice), { decimals: 0 })}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-content-secondary">{formatDZD(Number(b.cumul_fin_exercice), { decimals: 0 })}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">{formatDZD(Number(b.vnc_fin_exercice), { decimals: 0 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Modal open={genOpen} onOpenChange={setGenOpen} title="Générer les dotations d'une période"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setGenOpen(false)}>Annuler</Button>
            <Button onClick={() => genMut.mutate()} loading={genMut.isPending}>Générer</Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Mois"><Input type="number" step="1" min={1} max={12} value={genMois} onChange={(e) => setGenMois(Number(e.target.value))} /></FormField>
          <FormField label="Année"><Input type="number" step="1" value={genAnnee} onChange={(e) => setGenAnnee(Number(e.target.value))} /></FormField>
        </div>
        <p className="mt-2 text-xs text-content-muted">Crée les écritures BROUILLON D 681 / C 28x de la période (idempotent : une seule dotation par bien et par période). À valider en comptabilité.</p>
      </Modal>
    </div>
  );
}
